import axios from 'axios'
import pluralize from 'pluralize'

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again later.'
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.'

type TExportErrorResponse = {
  message?: string | null
  details?: string | string[] | null
}

const joinMessages = (...messages: (string | null | undefined)[]) => messages.filter(Boolean).join(' ')

const formatRetryAfter = (retryAfter: unknown) => {
  const seconds = Number(retryAfter)

  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'later'
  }
  if (seconds < 60) {
    return `in ${pluralize('second', Math.ceil(seconds), true)}`
  }
  return `in ${pluralize('minute', Math.ceil(seconds / 60), true)}`
}

export const getExportErrorMessage = (error: unknown): string => {
  // apiClient wraps every request failure in an AxiosError, including a 401 after a failed token refresh;
  // anything else is a bug on our side, e.g. while building the export payload
  if (!axios.isAxiosError(error)) {
    console.error('Failed to export results', error)
    return DEFAULT_ERROR_MESSAGE
  }
  if (!error.response) {
    return DEFAULT_ERROR_MESSAGE
  }

  const { status, headers } = error.response
  const { message, details } = (error.response.data ?? {}) as TExportErrorResponse

  switch (status) {
  case 400: {
    const detailsMessage = Array.isArray(details) ? details.join(' ') : details
    return detailsMessage || message || DEFAULT_ERROR_MESSAGE
  }
  case 401:
    return SESSION_EXPIRED_MESSAGE
  case 403:
    return message || 'You are not allowed to export these results.'
  case 404:
    return message || 'The requested data was not found.'
  case 429:
    return joinMessages(
      `You have reached the export limit. Please try again ${formatRetryAfter(headers?.['retry-after'])}.`,
      message
    )
  case 502:
    return message || 'We couldn\'t send the export email, please try again later.'
  case 503:
    return 'The server is busy, please try again shortly.'
  case 504:
    // The gateway gives up waiting while the export may still finish, so don't ask to retry right away
    return 'The export is taking longer than expected. If you don\'t receive the email in a few minutes, please try again.'
  default:
    return message || DEFAULT_ERROR_MESSAGE
  }
}
